import { ReactNode, useEffect, useRef } from "react";
import {
  formatPhoneNumber,
  hasPhoneNumber,
  toTelHref,
} from "../helper/phoneUtils";

type PhoneLinkProps = {
  phone?: string | number | null;
  /** Overrides the default link styling so callers can match their surroundings. */
  className?: string;
  /** Stops the click bubbling — needed inside grid rows that navigate on click. */
  stopPropagation?: boolean;
  /** Rendered when there is no callable number. */
  fallback?: ReactNode;
};

/**
 * Renders a phone number as a `tel:` anchor so it opens in the OS dialer.
 * Falls back to plain text when there is no usable number, so we never emit a
 * dead "tel:" href.
 */
const PhoneLink = ({
  phone,
  className = "a-link a-underline text-info",
  stopPropagation = false,
  fallback = "-",
}: PhoneLinkProps) => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const display = formatPhoneNumber(phone);
  const href = toTelHref(phone);
  const isCallable = hasPhoneNumber(phone) && href !== "";

  // ag-grid binds its row/cell click handlers as native DOM listeners, which run
  // during the real bubble phase — before React dispatches its synthetic events
  // from the root container. A React onClick handler therefore fires too late to
  // stop them, so the listener has to be native and attached to the anchor itself.
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!stopPropagation || !anchor) {
      return;
    }

    const handleClick = (event: MouseEvent) => event.stopPropagation();
    anchor.addEventListener("click", handleClick);
    return () => anchor.removeEventListener("click", handleClick);
  }, [stopPropagation, isCallable]);

  if (!isCallable) {
    return <>{fallback}</>;
  }

  return (
    <a
      ref={anchorRef}
      href={`tel:${href}`}
      className={className}
      aria-label={`Call ${display}`}
    >
      {display}
    </a>
  );
};

export default PhoneLink;
